import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export interface Activity {
  id: string;
  org_id: string;
  user_id: string | null;
  user_email: string;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  action: string;
  changes: any;
  created_at: string;
}

interface ActivityState {
  activities: Activity[];
  unreadCount: number;
  lastSeenAt: string | null;
  channel: RealtimeChannel | null;
  
  loadActivities: () => Promise<void>;
  markAllAsRead: () => void;
  subscribeToActivities: (orgId: string) => void;
  unsubscribe: () => void;
}

export const useActivityStore = create<ActivityState>((set, get) => ({
  activities: [],
  unreadCount: 0,
  lastSeenAt: null,
  channel: null,

  loadActivities: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('profiles')
        .select('org_id, preferences')
        .eq('user_id', user.id)
        .single();

      if (!profile?.org_id) return;

      // Get preferences
      const preferences = (profile.preferences as any) || {};
      const lastSeenAt = preferences.activity_last_seen_at || null;
      const showOwnActivities = preferences.show_own_activities !== false; // Default true

      // Fetch recent activities (last 24 hours, limit 50)
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      let query = supabase
        .from('activity_log')
        .select('*')
        .eq('org_id', profile.org_id)
        .gte('created_at', yesterday.toISOString());

      // Conditionally exclude own activities based on preference
      if (!showOwnActivities) {
        query = query.neq('user_id', user.id);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const activities = data || [];
      const unreadCount = lastSeenAt 
        ? activities.filter(a => new Date(a.created_at) > new Date(lastSeenAt)).length
        : activities.length;

      set({ 
        activities,
        unreadCount,
        lastSeenAt 
      });
    } catch (error) {
      console.error('Failed to load activities:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const now = new Date().toISOString();

      const { data: profile } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('user_id', user.id)
        .single();

      const currentPreferences = (profile?.preferences as any) || {};

      await supabase
        .from('profiles')
        .update({
          preferences: {
            ...currentPreferences,
            activity_last_seen_at: now,
          }
        })
        .eq('user_id', user.id);

      set({ unreadCount: 0, lastSeenAt: now });
    } catch (error) {
      console.error('Failed to mark activities as read:', error);
    }
  },

  subscribeToActivities: (orgId: string) => {
    const { channel } = get();
    if (channel) {
      channel.unsubscribe();
    }

    const newChannel = supabase
      .channel('activity-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'activity_log',
          filter: `org_id=eq.${orgId}`,
        },
        async (payload) => {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) return;

          // Get user preferences
          const { data: profile } = await supabase
            .from('profiles')
            .select('preferences')
            .eq('user_id', user.id)
            .single();

          const preferences = (profile?.preferences as any) || {};
          const showOwnActivities = preferences.show_own_activities !== false;

          const newActivity = payload.new as Activity;
          const isOwnActivity = newActivity.user_id === user.id;
          
          // If it's own activity and we don't want to show it, skip
          if (isOwnActivity && !showOwnActivities) return;

          const { activities, lastSeenAt } = get();
          const updatedActivities = [newActivity, ...activities].slice(0, 50);
          
          // Increment unread count if this is after last seen timestamp
          const isUnread = !lastSeenAt || new Date(newActivity.created_at) > new Date(lastSeenAt);
          
          set({ 
            activities: updatedActivities,
            unreadCount: isUnread ? get().unreadCount + 1 : get().unreadCount,
          });

          // Show toast notification for OTHER users' activities
          if (!isOwnActivity) {
            const entityTypeLabels: Record<string, string> = {
              sites: 'Site',
              zones: 'Zone',
              systems: 'System',
              endpoints: 'Endpoint',
              cloud_services: 'Cloud Service',
              cloud_resources: 'Cloud Resource',
              risks: 'Risk',
              controls: 'Control',
              incidents: 'Incident',
              evidence: 'Evidence',
              tasks: 'Task',
              suppliers: 'Supplier',
              internet_connections: 'Internet Connection',
              dmz_configs: 'DMZ Configuration',
              firewall_rules: 'Firewall Rule',
              org_layout: 'System Layout',
              pending_owner_assignments: 'Owner Assignment',
              wazuh_configs: 'Security Config',
              discovered_identities: 'Discovery',
              conduits: 'Conduit',
            };

            const entityLabel = entityTypeLabels[newActivity.entity_type] || newActivity.entity_type;
            const timeAgo = formatDistanceToNow(new Date(newActivity.created_at), { addSuffix: true });
            const userName = newActivity.user_email.split('@')[0]; // Use email prefix as fallback

            toast.info(`${entityLabel} ${newActivity.action}`, {
              description: `${timeAgo} · ${userName}${newActivity.entity_name ? '\n' + newActivity.entity_name : ''}`,
            });
          }
        }
      )
      .subscribe();

    set({ channel: newChannel });
  },

  unsubscribe: () => {
    const { channel } = get();
    if (channel) {
      channel.unsubscribe();
      set({ channel: null });
    }
  },
}));
